import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import authInstance from '../../../services/httpClient';
import { useMissionBuilder } from '../MissionBuilderContext';

const TARGET_TYPE_OPTIONS = [
  { value: 'random', label: 'Random products' },
  { value: 'category', label: 'By category' },
  { value: 'tags', label: 'By tags' },
  { value: 'out_of_stock', label: 'Out of stock' },
  { value: 'sold_recently', label: 'Sold recently' },
  { value: 'specific', label: 'Specific products' },
];

// ─── Shared hooks for category/tag lookups ──────────────────────────────────

function useCategoriesAndTags() {
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catsRes, tagsRes] = await Promise.allSettled([
          authInstance.get('/admin/categories?showInactive=true'),
          authInstance.get('/tags'),
        ]);
        if (cancelled) return;
        if (catsRes.status === 'fulfilled') {
          setCategories((catsRes.value.data.data || []).map((c: any) => ({ id: c.id, name: c.name })));
        }
        if (tagsRes.status === 'fulfilled') {
          setTags((tagsRes.value.data.data || []).map((t: { name: string }) => t.name));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { categories, tags, loading };
}

// ─── Component ──────────────────────────────────────────────────────────────

export function BlockConfigPanel() {
  const { blocks, selectedBlockId, updateBlockConfig } = useMissionBuilder();
  const block = blocks.find((b) => b.id === selectedBlockId);
  const { categories, tags: availableTags, loading: lookupLoading } = useCategoriesAndTags();

  if (!block) {
    return (
      <Box sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary" align="center" variant="body2">
          Select a block on the canvas to configure it
        </Typography>
      </Box>
    );
  }

  const update = (key: string, value: any) => updateBlockConfig(block.id, { [key]: value });

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700 }}>
        Configure: {block.label}
      </Typography>

      {/* ── Trigger configs ── */}
      {block.type === 'idle_time' && (
        <TextField
          fullWidth
          label="Idle Minutes"
          type="number"
          size="small"
          value={block.config.idleMinutes || ''}
          onChange={(e) => update('idleMinutes', parseInt(e.target.value) || 0)}
          helperText="Trigger after picker has been idle for this many minutes"
          sx={{ mb: 2 }}
        />
      )}

      {block.type === 'interval' && (
        <TextField
          fullWidth
          label="Interval (minutes)"
          type="number"
          size="small"
          value={block.config.intervalMinutes || ''}
          onChange={(e) => update('intervalMinutes', parseInt(e.target.value) || 0)}
          helperText="Trigger every N minutes"
          sx={{ mb: 2 }}
        />
      )}

      {block.type === 'time_of_day' && (
        <TextField
          fullWidth
          label="Time"
          type="time"
          size="small"
          value={block.config.timeOfDay || '09:00'}
          onChange={(e) => update('timeOfDay', e.target.value)}
          helperText="Trigger at this time each day"
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
      )}

      {block.type === 'manual' && (
        <Typography variant="body2" color="text.secondary">
          Manual missions are triggered by an admin from this dashboard.
        </Typography>
      )}

      {/* ── Mission type (target) configs ── */}
      {block.category === 'mission' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Target Type</InputLabel>
            <Select
              value={block.config.targetType || 'random'}
              label="Target Type"
              onChange={(e) => update('targetType', e.target.value)}
            >
              {TARGET_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Count (for most target types) */}
          {block.config.targetType !== 'specific' && (
            <TextField
              fullWidth
              label="Product Count"
              type="number"
              size="small"
              value={block.config.count || 10}
              onChange={(e) => update('count', parseInt(e.target.value) || 10)}
              helperText="Number of products to include"
              sx={{ mb: 2 }}
              inputProps={{ min: 1, max: 100 }}
            />
          )}

          {/* Category dropdown */}
          {block.config.targetType === 'category' && (
            <Autocomplete
              size="small"
              options={categories}
              getOptionLabel={(opt) => opt.name}
              loading={lookupLoading}
              value={categories.find((c) => c.id === block.config.categoryId) || null}
              onChange={(_, val) => update('categoryId', val?.id || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  helperText="Select the product category to target"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {lookupLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 2 }}
            />
          )}

          {/* Tags autocomplete */}
          {block.config.targetType === 'tags' && (
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={availableTags}
              loading={lookupLoading}
              value={block.config.tags || []}
              onChange={(_, val) => update('tags', val)}
              renderTags={(value, getTagProps) =>
                value.map((tag: string, index: number) => (
                  <Chip variant="outlined" label={tag} size="small" {...getTagProps({ index })} key={tag} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  helperText="Select tags or type custom ones"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {lookupLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 2 }}
            />
          )}

          {/* Sold recently */}
          {block.config.targetType === 'sold_recently' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Sold Within</InputLabel>
              <Select
                value={block.config.soldWithin || '7d'}
                label="Sold Within"
                onChange={(e) => update('soldWithin', e.target.value)}
              >
                <MenuItem value="1d">Last 24 hours</MenuItem>
                <MenuItem value="3d">Last 3 days</MenuItem>
                <MenuItem value="7d">Last 7 days</MenuItem>
                <MenuItem value="14d">Last 14 days</MenuItem>
                <MenuItem value="30d">Last 30 days</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Target type chip */}
          <Box sx={{ mt: 1 }}>
            <Chip
              label={TARGET_TYPE_OPTIONS.find((o) => o.value === block.config.targetType)?.label || 'Random'}
              size="small"
              color="info"
              variant="outlined"
            />
          </Box>
        </>
      )}

      {/* ── Step configs ── */}
      {block.category === 'step' && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={block.config.required !== false}
                onChange={(e) => update('required', e.target.checked)}
              />
            }
            label="Required step"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Instructions"
            size="small"
            value={block.config.instructions || ''}
            onChange={(e) => update('instructions', e.target.value)}
            helperText="Instructions shown to the picker for this step"
          />
        </>
      )}
    </Box>
  );
}
